/**
 * Olfactory Discovery — interactive tab/filter logic
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /*  Note & Mood keyword maps                                           */
  /* ------------------------------------------------------------------ */
  const NOTE_KEYWORDS = {
    oud:      ['oud', 'agarwood', 'agar wood', 'oudh'],
    amber:    ['amber', 'ambre', 'benzoin', 'labdanum', 'resin', 'tonka', 'balsam'],
    floral:   ['rose', 'jasmine', 'jasmin', 'gardenia', 'peony', 'tuberose', 'lily', 'magnolia', 'neroli', 'osmanthus', 'freesia', 'ylang', 'floral', 'flower'],
    fruity:   ['berry', 'berries', 'apple', 'peach', 'pear', 'plum', 'cherry', 'mango', 'passion fruit', 'tropical', 'blackcurrant', 'cassis', 'raspberry', 'strawberry', 'lychee'],
    fresh:    ['citrus', 'bergamot', 'lemon', 'lime', 'grapefruit', 'mandarin', 'orange', 'aquatic', 'marine', 'sea', 'water', 'green', 'bamboo', 'fern', 'tea', 'mint'],
    gourmand: ['vanilla', 'caramel', 'chocolate', 'praline', 'coffee', 'cocoa', 'almond', 'sugar', 'honey', 'butterscotch', 'toffee'],
    woody:    ['sandalwood', 'cedar', 'vetiver', 'patchouli', 'woody', 'wood', 'woods', 'guaiac', 'birch', 'pine', 'fir', 'oakmoss', 'moss'],
    powdery:  ['musk', 'iris', 'violet', 'orris', 'powder', 'powdery', 'talc', 'cotton', 'linen', 'soft', 'skin'],
    leather:  ['leather', 'suede', 'tobacco', 'smoke', 'smoky', 'birch tar'],
    spicy:    ['saffron', 'pepper', 'cinnamon', 'spice', 'spicy', 'clove', 'cardamom', 'ginger', 'nutmeg', 'anise', 'incense', 'myrrh'],
  };

  const MOOD_KEYWORDS = {
    'romantic':   [...NOTE_KEYWORDS.floral, ...NOTE_KEYWORDS.amber, 'oud', 'vanilla', 'rose', 'jasmine'],
    'confident':  [...NOTE_KEYWORDS.oud, ...NOTE_KEYWORDS.leather, 'cedar', 'vetiver', 'pepper', 'tobacco'],
    'fresh-light':[...NOTE_KEYWORDS.fresh, 'green tea', 'mint', 'lemon', 'lime'],
    'mysterious': [...NOTE_KEYWORDS.oud, ...NOTE_KEYWORDS.spicy, 'patchouli', 'smoke', 'incense', 'myrrh'],
    'cozy':       [...NOTE_KEYWORDS.amber, ...NOTE_KEYWORDS.gourmand, 'sandalwood', 'musk', 'tonka'],
    'joyful':     [...NOTE_KEYWORDS.fresh, ...NOTE_KEYWORDS.fruity, 'jasmine', 'peach', 'berry'],
  };

  const MOOD_LABELS = {
    'romantic':   'Romantic & Sensual',
    'confident':  'Confident & Powerful',
    'fresh-light':'Fresh & Light',
    'mysterious': 'Mysterious & Dark',
    'cozy':       'Cozy & Comforting',
    'joyful':     'Bright & Joyful',
  };

  const NOTE_LABELS = {
    oud:      'Oud',
    amber:    'Amber',
    floral:   'Floral',
    fruity:   'Fruity',
    fresh:    'Fresh',
    gourmand: 'Gourmand',
    woody:    'Woody',
    powdery:  'Powdery & Musky',
    leather:  'Leather',
    spicy:    'Spicy',
  };

  /* ------------------------------------------------------------------ */
  /*  Load product data                                                  */
  /* ------------------------------------------------------------------ */
  let allProducts = [];

  const dataEl = document.getElementById('od-products-data');
  if (dataEl) {
    try {
      allProducts = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[OD] Failed to parse product data:', e);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  DOM references                                                     */
  /* ------------------------------------------------------------------ */
  const tabs        = document.querySelectorAll('.od-tab');
  const panels      = document.querySelectorAll('.od-panel');
  const productsGrid = document.getElementById('od-products-grid');
  const filterBar   = document.getElementById('od-filter-bar');
  const filterName  = document.getElementById('od-filter-name');
  const filterClear = document.getElementById('od-filter-clear');
  const resultsCount = document.getElementById('od-results-count');

  if (!tabs.length) return; // Not on this page

  /* ------------------------------------------------------------------ */
  /*  Tab switching                                                       */
  /* ------------------------------------------------------------------ */
  function showTab(tabName) {
    tabs.forEach(function (btn) {
      const active = btn.dataset.tab === tabName;
      btn.classList.toggle('od-tab--active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    panels.forEach(function (panel) {
      const panelId = 'od-panel-' + tabName;
      if (panel.id === panelId) {
        panel.removeAttribute('hidden');
        panel.classList.add('od-panel--active');
      } else {
        panel.setAttribute('hidden', '');
        panel.classList.remove('od-panel--active');
      }
    });
  }

  tabs.forEach(function (btn) {
    btn.addEventListener('click', function () {
      showTab(btn.dataset.tab);
    });
  });

  /* ------------------------------------------------------------------ */
  /*  Product filtering                                                  */
  /* ------------------------------------------------------------------ */
  function notesText(product) {
    return [
      product.topNotes || '',
      product.middleNotes || '',
      product.baseNotes || '',
      product.title || '',
    ].join(' ').toLowerCase();
  }

  function filterByKeywords(keywords) {
    return allProducts.filter(function (p) {
      const text = notesText(p);
      return keywords.some(function (kw) {
        return text.includes(kw.toLowerCase());
      });
    });
  }

  function renderProducts(products, filterLabel) {
    if (!productsGrid) return;

    // Update filter bar
    if (filterBar && filterName) {
      if (filterLabel) {
        filterName.textContent = filterLabel;
        filterBar.classList.add('od-filter-bar--visible');
      } else {
        filterBar.classList.remove('od-filter-bar--visible');
        filterName.textContent = '';
      }
    }

    // Update count
    if (resultsCount) {
      if (filterLabel) {
        resultsCount.textContent = products.length + ' perfume' + (products.length !== 1 ? 's' : '') + ' found';
      } else {
        resultsCount.textContent = products.length + ' perfume' + (products.length !== 1 ? 's' : '') + ' in collection';
      }
    }

    if (!products.length) {
      productsGrid.innerHTML =
        '<div class="od-no-results">' +
          '<strong>No perfumes found</strong>' +
          '<p>Try exploring a different note or mood family.</p>' +
        '</div>';
      return;
    }

    productsGrid.innerHTML = products.map(function (p, i) {
      const delay = Math.min(i * 0.04, 0.5);
      const notesSnippet = [p.topNotes, p.middleNotes, p.baseNotes]
        .filter(Boolean).join(' · ');

      const imageHtml = p.image
        ? '<img src="' + p.image + '" alt="' + escapeHtml(p.title) + '" loading="lazy">'
        : '<div class="od-product-card__placeholder">&#x2234;</div>';

      const imageLinkOrDiv = p.image
        ? '<a class="od-product-card__image-link" href="' + p.url + '">' + imageHtml + '</a>'
        : '<div class="od-product-card__image-link">' + imageHtml + '</div>';

      return (
        '<div class="od-product-card" style="animation-delay:' + delay + 's">' +
          imageLinkOrDiv +
          '<div class="od-product-card__body">' +
            '<a class="od-product-card__title" href="' + p.url + '">' + escapeHtml(p.title) + '</a>' +
            '<p class="od-product-card__price">' + p.price + '</p>' +
            (notesSnippet ? '<p class="od-product-card__notes">' + escapeHtml(notesSnippet) + '</p>' : '') +
            '<a class="od-product-card__btn" href="' + p.url + '">View Perfume</a>' +
          '</div>' +
        '</div>'
      );
    }).join('');
  }

  function showAllPerfumes() {
    renderProducts(allProducts, null);
  }

  /* ------------------------------------------------------------------ */
  /*  Note card CTAs                                                     */
  /* ------------------------------------------------------------------ */
  document.querySelectorAll('.od-note-card__cta').forEach(function (btn) {
    btn.addEventListener('click', function () {
      const noteId = btn.dataset.note;
      const keywords = NOTE_KEYWORDS[noteId] || [];
      const label = NOTE_LABELS[noteId] || noteId;
      const filtered = filterByKeywords(keywords);
      renderProducts(filtered, label);
      showTab('perfumes');
    });
  });

  /* ------------------------------------------------------------------ */
  /*  Mood card CTAs                                                     */
  /* ------------------------------------------------------------------ */
  document.querySelectorAll('.od-mood-card__cta').forEach(function (btn) {
    btn.addEventListener('click', function () {
      const moodId = btn.dataset.mood;
      const keywords = MOOD_KEYWORDS[moodId] || [];
      const label = MOOD_LABELS[moodId] || moodId;
      const filtered = filterByKeywords(keywords);
      renderProducts(filtered, label);
      showTab('perfumes');
    });
  });

  /* ------------------------------------------------------------------ */
  /*  Clear filter                                                       */
  /* ------------------------------------------------------------------ */
  if (filterClear) {
    filterClear.addEventListener('click', function () {
      showAllPerfumes();
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Init: pre-populate perfumes panel                                  */
  /* ------------------------------------------------------------------ */
  showAllPerfumes();

  /* ------------------------------------------------------------------ */
  /*  Utility                                                            */
  /* ------------------------------------------------------------------ */
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ------------------------------------------------------------------ */
  /*  Page loader dismiss                                                 */
  /* ------------------------------------------------------------------ */
  (function () {
    const loader = document.getElementById('od-loader');
    if (!loader) return;

    function dismiss() {
      loader.classList.add('od-loader--done');
      loader.addEventListener('transitionend', function () {
        loader.style.display = 'none';
      }, { once: true });
    }

    if (document.readyState === 'complete') {
      setTimeout(dismiss, 150);
    } else {
      window.addEventListener('load', function () { setTimeout(dismiss, 150); });
    }
  }());

})();
